import { useLocalStorage } from "@rehooks/local-storage";
import { ReactNode, useCallback, useMemo } from "react";

import { ExternalSortManagement } from "components/Filters/types";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import { TableGenericStore } from "ds/components/Table/store/useTableStore/types";
import TableSimple from "ds/components/TableSimple";
import { TableCellPerColumn, TableSimpleDataItem } from "ds/components/TableSimple/types";
import useAnalytics from "hooks/useAnalytics";

import styles from "./styles.module.css";

type UsedBySectionProps<T extends TableSimpleDataItem> = {
  items: Array<T>;
  store: TableGenericStore;
  title: string;
  integrationName: string;
  cells: TableCellPerColumn<T>;
  filtersStorageKey: string;
  sortConfig: Record<string, string>;
  emptyState: ReactNode;
  externalSortManagement: ExternalSortManagement;
};

const CloudUsedBySection = <T extends TableSimpleDataItem>({
  items,
  store,
  title,
  integrationName,
  cells,
  filtersStorageKey,
  sortConfig,
  emptyState,
  externalSortManagement,
}: UsedBySectionProps<T>) => {
  const id = useMemo(
    () =>
      `${integrationName}-${title.toLowerCase().replaceAll(" ", "-")}-used-by-section-collapsed`,
    [integrationName, title]
  );
  const [isCollapsed, setIsCollapsed] = useLocalStorage<boolean>(id, items.length === 0);
  const handleToggle = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed, setIsCollapsed]);

  const trackAnalytics = useAnalytics();

  return (
    <>
      <CardCollapsible
        fullWidth
        className={styles.collapsibleWrapper}
        title={title}
        titleSuffix={<Counter count={items.length} size="small" />}
        isCollapsed={isCollapsed}
        onToggle={handleToggle}
        ariaLevel={2}
        ariaLabel={title}
        fullbleed
      >
        <TableSimple
          data={items}
          loading={false}
          store={store}
          wrapperClassName={styles.tableWrapper}
          ariaLabel={`${integrationName} used by list: ${title}`}
          trackAnalytics={trackAnalytics}
          cellPerColumn={cells}
          filtersStorageKey={filtersStorageKey}
          sortConfig={sortConfig}
          externalSortManagement={externalSortManagement}
          emptyState={emptyState}
          hasPagination={false}
        />
      </CardCollapsible>
    </>
  );
};

export default CloudUsedBySection;
